import React, { useState, useMemo, useEffect, useRef } from "react"; // Added useRef
import { useSelector, useDispatch } from "react-redux";
// import PostFeedModal from "../../../../Community/PostFeed/PostFeedModal";
import {
  deleteFeed,
  getMyFeeds,
  updateFeed,
} from "../../../../Redux/Reducers/communityReducer";
import FeedIcon from "../../../../Assets/css/Community/FeedIcon.webp";
import FeedCard from "./FeedCard";
import usePagination from "./usePagination";
import FeedPostSkeleton from "../../../LoadingSkeleton/Events/FeedPostSkeleton";
import PostFeedModal from "../Components/PostFeedModal";

const MyFeeds = () => {
  const dispatch = useDispatch();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const exploreType = useSelector((state) => state?.community?.exploreType);
  const [editFeed, setEditFeed] = useState(null);
  const loaderRef = useRef(); // Create ref for Intersection Observer

  // Memoize fetchParams to prevent unnecessary re-renders
  const fetchParams = useMemo(() => ({
    type: exploreType
  }), [exploreType]);

  const {
    data: feedsData,
    loading: loadingFeeds,
    hasMore,
    handleLoadMore,
    handleRefresh,
  } = usePagination(
    (state) => state?.community?.feeds,
    getMyFeeds,
    fetchParams
  );

  // console.log("feedsDataOOO", feedsData)

  // Add Intersection Observer for infinite scroll
  useEffect(() => {
    const currentLoader = loaderRef.current;
    
    if (!currentLoader || !hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingFeeds) {
          handleLoadMore();
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );

    observer.observe(currentLoader);
    
    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [hasMore, loadingFeeds, handleLoadMore]);

  // Refresh feeds when exploreType changes
  useEffect(() => {
    handleRefresh();
  }, [exploreType]);

  const handleEdit = (item) => {
    setEditFeed(item);
    setEditModalOpen(true);
  };

  const handleDelete = async (item) => {
    await dispatch(deleteFeed(item.id));
    handleRefresh(); // Refresh list after deletion
  };

  const handleUpdatePost = async (updatedContent, updatedMedia) => {
    const payload = new FormData();
    payload.append("content", updatedContent);
    payload.append("is_visible", true);
    
    updatedMedia.forEach((mediaItem) => {
      if (mediaItem.file) {
        payload.append("media", mediaItem.file);
      } else {
        payload.append("existing_media", mediaItem.preview || mediaItem.url);
      }
    });
    
    await dispatch(updateFeed({ id: editFeed.id, data: payload }));
    setEditModalOpen(false);
    handleRefresh(); // Refresh list after update
  };

  // Show loading state during initial load
  if (loadingFeeds && feedsData.length === 0) {
    return (
      <div>
        <FeedPostSkeleton />
        <FeedPostSkeleton />
      </div>
    );
  }

  // Show empty state when no feeds
  if (feedsData.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
          textAlign: "center",
          color: "#555",
        }}
      >
        <img
          src={FeedIcon}
          alt="No Posts"
          style={{ width: "80px", marginBottom: "16px", opacity: 0.8 }}
        />
        <p style={{ fontSize: "14px" }}>No Posts Found</p>
      </div>
    );
  }

  return (
    <>
      {feedsData.map((item) => (
        <FeedCard
          key={item.id}
          item={item}
          is_my_post={true}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ))}

      {/* Show loading indicator when fetching more */}
      {loadingFeeds && <FeedPostSkeleton />}

      {/* Invisible loader element */}
      <div ref={loaderRef} style={{ height: "1px", width: "100%" }} />

      {/* Show end of list message */}
      {!hasMore && feedsData.length > 0 && (
        <p style={{ textAlign: 'center', padding: '10px', color: '#777' }}>
          You've reached the end of posts
        </p>
      )}

      {editModalOpen && (
        <PostFeedModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          initialContent={editFeed?.content}
          initialMedia={editFeed?.media}
          onSubmit={handleUpdatePost}
          isEditMode={true}
          postId={editFeed?.id}
          author={editFeed?.author}
        />
      )}
    </>
  );
};

export default MyFeeds;